/**
 * @fileoverview Per-row source-URL health check for the Documents migration grid.
 * @module system-web/components/migration/DocumentHealthCheckButton
 *
 * Responsibilities:
 * - Probe a row's unsigned sourceUrl via legacyMigrationService.
 * - Render Reachable / Unreachable / SQL-binary without leaking URLs.
 *
 * Dependencies:
 * - lucide-react
 * - probeLegacyFileHealth
 *
 * Architecture:
 * - Online exception. Result is component-local state.
 *
 * @component
 * Purpose: Health-check button on a Documents grid row.
 * Props: sourceUrl (unsigned; never logged)
 * Behavior: idle → probing → ok/errorType
 * Data Source: remote (system-api health-check)
 * Constraints: never display the URL or SAS
 * Performance: on-demand per row
 */

import { useState } from 'react';
import { Loader2, CheckCircle2, XCircle } from 'lucide-react';
import { probeLegacyFileHealth } from '../../services/legacyMigrationService';

function labelFor(errorType?: string): string {
  if (!errorType) return 'Reachable';
  if (errorType === 'empty_source') return 'SQL binary';
  if (errorType === 'sas_not_configured') return 'Not configured';
  if (errorType === 'sas_expired') return 'SAS expired';
  if (errorType === 'http_404') return 'Missing';
  return 'Unreachable';
}

/**
 * @component DocumentHealthCheckButton
 */
export function DocumentHealthCheckButton({
  sourceUrl,
}: {
  sourceUrl: string | null | undefined;
}): JSX.Element {
  const [state, setState] = useState<'idle' | 'probing' | 'ok' | 'fail'>('idle');
  const [errorType, setErrorType] = useState<string | undefined>();

  const run = async () => {
    setState('probing');
    try {
      const result = await probeLegacyFileHealth(sourceUrl);
      setErrorType(result.errorType);
      setState(result.ok ? 'ok' : 'fail');
    } catch {
      setErrorType('unreadable_source');
      setState('fail');
    }
  };

  if (state === 'probing') {
    return (
      <span className="inline-flex items-center gap-1 text-[10px] text-gray-500">
        <Loader2 className="h-3 w-3 animate-spin" /> Checking
      </span>
    );
  }
  if (state === 'ok') {
    return (
      <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-emerald-700">
        <CheckCircle2 className="h-3 w-3" /> Reachable
      </span>
    );
  }
  if (state === 'fail') {
    return (
      <button
        type="button"
        onClick={() => void run()}
        className="inline-flex items-center gap-1 text-[10px] font-semibold text-red-700 hover:underline"
      >
        <XCircle className="h-3 w-3" /> {labelFor(errorType)}
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={() => void run()}
      className="text-[10px] font-medium text-orange-700 hover:underline"
    >
      Check
    </button>
  );
}
